import {Button, FormControl, FormField, OptionType, Separator} from 'platform/components';
import {Grid, HStack, Show, VStack} from 'platform/foundation';

import {UseFormReturn, useWatch} from 'react-hook-form';

import {isEmpty} from 'ramda';

import {
  GetServiceOrderIntegrationsMazdaPreauthorizationApiResponse,
  useGetMazdaWarrantyCausalPartsQuery,
  useLazyGetServiceOrderIntegrationsMazdaPreauthorizationQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';

import {TestIdProps, suffixTestId} from 'shared/web';

import {getMazdaCatalogOptions} from '../../../../../utils/getMazdaCatalogOptions';
import {getMazdaCodeDescriptionLabel} from '../../../../../utils/getMazdaCodeDescriptionLabel';
import {MazdaClaimSendFormType} from '../types/MazdaClaimSendFormType';
import {hasMazdaWarrantyInstallationDetails} from '../utils/hasMazdaWarrantyInstallationDetails';
import {
  hasMazdaPreauthorizationData,
  mapMazdaPreauthorizationToClaimValues,
} from '../utils/mapMazdaPreauthorizationToClaimValues';

type MazdaPreauthorization = GetServiceOrderIntegrationsMazdaPreauthorizationApiResponse;

const SCOPE_OPTIONS = [
  {value: 'warranty', label: i18n.t('entity.orderIntegration.labels.scopeWarranty')},
  {value: 'goodwill', label: i18n.t('entity.orderIntegration.labels.scopeGoodwill')},
];

const WARRANTY_TYPE_OPTIONS = [
  {value: 'basic_warranty', label: i18n.t('entity.orderIntegration.labels.warrantyTypeBasic')},
  {
    value: 'predelivery_inspection',
    label: i18n.t('entity.orderIntegration.labels.warrantyTypePredeliveryInspection'),
  },
  {
    value: 'part_warranty',
    label: i18n.t('entity.orderIntegration.labels.warrantyTypePartWarranty'),
  },
  {
    value: 'repeated_repair',
    label: i18n.t('entity.orderIntegration.labels.warrantyTypeRepeatedRepair'),
  },
];

interface MazdaClaimStandardFieldsProps extends TestIdProps {
  control: FormControl<MazdaClaimSendFormType>;
  formApi: UseFormReturn<MazdaClaimSendFormType>;
  maintenanceOptions: OptionType[];
  serviceCaseId: string;
  serviceOrderId: string;
  isPreauthorizationAvailable: boolean;
}

export function MazdaClaimStandardFields(props: MazdaClaimStandardFieldsProps) {
  const {data: causalParts} = useGetMazdaWarrantyCausalPartsQuery();

  const [getPreauthorization, {isLoading: isApplyingPreauthorization}] =
    useLazyGetServiceOrderIntegrationsMazdaPreauthorizationQuery();

  const [isSpecialCausalPart, warrantyType] = useWatch({
    control: props.formApi.control,
    name: ['isSpecialCausalPart', 'warrantyType'],
  });

  const hasInstallationDetails = hasMazdaWarrantyInstallationDetails(warrantyType);

  const applyPreauthorization = (preauthorization: MazdaPreauthorization) => {
    if (!hasMazdaPreauthorizationData(preauthorization)) {
      return;
    }

    const values = mapMazdaPreauthorizationToClaimValues(preauthorization);

    if (isEmpty(values)) {
      return;
    }

    props.formApi.reset({...props.formApi.getValues(), ...values});
  };

  const handleApplyPreauthorization = () =>
    getPreauthorization({
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    })
      .unwrap()
      .then(applyPreauthorization)
      .catch(handleApiError);

  return (
    <VStack spacing={4}>
      <Show when={props.isPreauthorizationAvailable}>
        <HStack>
          <Button
            variant="link"
            size="small"
            leftIcon="action/input"
            title={i18n.t('entity.orderIntegration.actions.applyFromPreauthorization')}
            onClick={handleApplyPreauthorization}
            isLoading={isApplyingPreauthorization}
            isDisabled={isApplyingPreauthorization}
            data-testid={suffixTestId('applyFromPreauthorization', props)}
          />
        </HStack>
        <Separator spacing={0} />
      </Show>
      <Grid spacing={4} columns={2}>
        <FormField
          control={props.control}
          name="scope"
          type="choice"
          label={i18n.t('entity.orderIntegration.labels.scope')}
          options={SCOPE_OPTIONS}
          menuInPortal
          isNotClearable
          isRequired
          data-testid={suffixTestId('scope', props)}
        />
        <FormField
          control={props.control}
          name="warrantyType"
          type="choice"
          label={i18n.t('entity.orderIntegration.labels.warrantyType')}
          options={WARRANTY_TYPE_OPTIONS}
          menuInPortal
          isNotClearable
          isRequired
          data-testid={suffixTestId('warrantyType', props)}
        />
        <FormField
          control={props.control}
          name="mileage"
          type="number"
          label={i18n.t('entity.integration.labels.mileage')}
          suffix={i18n.t('general.metric.km')}
          isRequired
          data-testid={suffixTestId('mileage', props)}
        />
        <FormField
          control={props.control}
          name="repairDate"
          type="apiDate"
          label={i18n.t('entity.orderIntegration.labels.repairDate')}
          isRequired
          data-testid={suffixTestId('repairDate', props)}
        />
        <Show when={hasInstallationDetails}>
          <FormField
            control={props.control}
            name="installationDate"
            type="apiDate"
            label={i18n.t('entity.serviceOrder.labels.installationDate')}
            isRequired
            data-testid={suffixTestId('installationDate', props)}
          />
          <FormField
            control={props.control}
            name="installationMileage"
            type="number"
            label={i18n.t('entity.serviceOrder.labels.installationMileage')}
            suffix={i18n.t('general.metric.km')}
            isRequired
            data-testid={suffixTestId('installationMileage', props)}
          />
          <FormField
            control={props.control}
            name="installationRepairNo"
            type="text"
            label={i18n.t('entity.orderIntegration.labels.installationRepairNo')}
            isRequired
            data-testid={suffixTestId('installationRepairNo', props)}
          />
        </Show>
      </Grid>
      <FormField
        control={props.control}
        name="vehicleServicedAccordingToManufacturerSpecs"
        type="radio"
        label={i18n.t('entity.orderIntegration.labels.vehicleServicedAccordingToSpecs')}
        options={props.maintenanceOptions}
        isRequired
        data-testid={suffixTestId('vehicleServicedAccordingToSpecs', props)}
      />
      <Separator spacing={0} />
      <FormField
        control={props.control}
        name="causalPart"
        type="textarea"
        minRows={1}
        label={i18n.t('entity.orderIntegration.labels.causalPart')}
        isRequired={!isSpecialCausalPart}
        isDisabled={isSpecialCausalPart}
        data-testid={suffixTestId('causalPart', props)}
      />
      <FormField
        control={props.control}
        name="isSpecialCausalPart"
        type="switch"
        label={i18n.t('entity.orderIntegration.labels.specialCausalPart')}
        onChange={(value) => {
          if (!value) {
            props.formApi.setValue('specialCausalPart', null);
          }
          props.formApi.setValue('causalPart', null);
        }}
        data-testid={suffixTestId('isSpecialCausalPart', props)}
      />
      <FormField
        control={props.control}
        name="specialCausalPart"
        type="choice"
        label={i18n.t('entity.orderIntegration.labels.specialCausalPart')}
        options={getMazdaCatalogOptions(causalParts?.causalParts, getMazdaCodeDescriptionLabel)}
        menuInPortal
        isRequired={isSpecialCausalPart}
        isDisabled={!isSpecialCausalPart}
        data-testid={suffixTestId('specialCausalPart', props)}
      />
      <FormField
        control={props.control}
        name="message"
        type="textarea"
        minRows={3}
        label={i18n.t('general.labels.message')}
        data-testid={suffixTestId('message', props)}
      />
    </VStack>
  );
}
